import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { can, useMyRights } from "../lib/rights";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Search, MoreVertical, ChevronRight, X, Save, Loader2,
  Users, Target, Trash2, Plus, ArrowRight, Briefcase,
  FileText, Construction,
} from "lucide-react";

/**
 * Lead Management — visual redesign to mirror kinntegra.co.in.
 *
 * Layout (verified against `/kinntegra-old/kinntegra-webapp/.../leads.component.html`):
 *   - Top bar:  [ + New Lead ]                    (right side blank / breadcrumb)
 *   - Nav tabs (underline style): Leads | Introduction | Comprehensive Analysis | Accounts
 *   - Right rail: search box
 *   - Data table: Family/Client Name · Associate Name · Location · Date(DD-MM-YY) · Actions · [⋯]
 *   - Actions cell:  "Proceed to:"  <coloured badge pill with arrow>  (+ separator + second pill on Intro/Accounts tabs)
 *   - Comprehensive tab: "Work In Progress" placeholder (matches original — feature was never built in kinntegra either)
 *
 * All existing lifecycle endpoints (create / advance-intro / advance-comp /
 * BSE stub / delete) preserved. Only look-and-feel changed.
 */

const TABS = [
  { key: 1, label: "Leads",                  api: 1 },
  { key: 2, label: "Introduction",           api: 2 },
  { key: 3, label: "Comprehensive Analysis", api: 3 },
  { key: 4, label: "Accounts",               api: 4 },
];

const EMPTY_LEAD = {
  associate_id: "", associate_name: "",
  first_name: "", last_name: "", gender: "", dob: "",
  mobile: "", email: "", country: "India",
  address1: "", address2: "", address3: "",
  city: "", state: "", pin_code: "",
};

// Pill colours mirror kinntegra.co.in (light mint/teal accents on white)
const PILL = {
  info:    "bg-teal-50 text-teal-700 border-teal-200",
  danger:  "bg-rose-50 text-rose-700 border-rose-200",
  warning: "bg-amber-50 text-amber-800 border-amber-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

// Basic client-side validators. Return "" if OK, else an error message.
// Messages match Lead_Creation_error_report1.docx spec.
const V = {
  email:    (v) => !v ? "" : (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? "" : "Please enter a valid Email ID."),
  pincode:  (v) => !v ? "" : (/^\d{6}$/.test(v) ? "" : "Please select a valid Pincode."),
  dobPast:  (v) => !v ? "" : (new Date(v) < new Date() ? "" : "DOB must be in the past"),
};

export default function Leads() {
  const { api, user } = useAuth();
  const myRights = useMyRights(user);
  const navigate = useNavigate();
  const [tab, setTab] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [newLeadInitial, setNewLeadInitial] = useState(null); // null → hidden; {} → new; row → edit
  const [editLead, setEditLead] = useState(null);
  const [drawerStage, setDrawerStage] = useState(1);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);   // row awaiting deactivate confirmation

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/lead-mgmt/list?stage=${tab}&include_deleted=${showDeleted ? "true" : "false"}`
      );
      setRows(data?.data || []);
    } catch (e) {
      toast.error("Failed to load leads: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tab, showDeleted]);

  const filtered = useMemo(() => {
    const dateOf = (r) => (tab === 4 ? (r.account?.pushed_at || r.updated_at) : r.lead_date) || "";
    const base = q.trim()
      ? rows.filter(r => {
          const needle = q.toLowerCase().trim();
          return (
            `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase().includes(needle) ||
            (r.email || "").toLowerCase().includes(needle) ||
            (r.mobile || "").toLowerCase().includes(needle) ||
            (r.pin_code || "").toLowerCase().includes(needle) ||
            (r.city || "").toLowerCase().includes(needle) ||
            (r.state || "").toLowerCase().includes(needle)
          );
        })
      : rows;
    return [...base].sort((a, b) => (dateOf(b) || "").localeCompare(dateOf(a) || ""));
  }, [rows, q, tab]);

  const openLead = (row, stage = 1) => {
    // Stage 2 (Introduction) opens a full-page Data Gathering view.
    if (stage === 2) { navigate(`/leads/${row.id}/introduction`); return; }
    // Stage 1 (Prospect / Edit Lead) opens the same New Lead modal, prefilled.
    if (stage === 1) { setNewLeadInitial(row); return; }
    setEditLead(row); setDrawerStage(stage);
  };

  const onDelete = (row) => setDeleteTarget(row);

  const confirmDelete = async () => {
    const row = deleteTarget;
    if (!row) return;
    try {
      await api.delete(`/lead-mgmt/${row.id}`);
      toast.success("Lead deactivated. Toggle 'Show deactivated' to reactivate.");
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      toast.error("Deactivate failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const onReactivate = async (row) => {
    try {
      await api.post(`/lead-mgmt/${row.id}/reactivate`);
      toast.success("Lead reactivated.");
      refresh();
    } catch (e) {
      toast.error("Reactivate failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const s = iso.length >= 10 ? iso.slice(0, 10) : iso;
    const [y, m, d] = s.split("-");
    const day = parseInt(d, 10);
    if (!day || !y || !m) return iso;
    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sept","Oct","Nov","Dec"];
    const month = MONTHS[parseInt(m, 10) - 1] || m;
    const suf = (n) => {
      const v = n % 100;
      if (v >= 11 && v <= 13) return "th";
      switch (n % 10) { case 1: return "st"; case 2: return "nd"; case 3: return "rd"; default: return "th"; }
    };
    return `${day}${suf(day)} ${month}, ${y}`;
  };

  const daysLapsed = (iso) => {
    if (!iso) return null;
    const s = iso.length >= 10 ? iso.slice(0, 10) : iso;
    const t = Date.parse(s + "T00:00:00Z");
    if (Number.isNaN(t)) return null;
    const now = Date.now();
    const diff = Math.floor((now - t) / (24 * 60 * 60 * 1000));
    return diff < 0 ? 0 : diff;
  };
  const familyName = (r) => (r.introduction?.family_name || `${r.first_name || ""} ${r.last_name || ""}`.trim());

  return (
    <div className="px-4 py-6" data-testid="leads-page">
      {/* Top bar — tabs + search + show-deactivated inline with NEW LEAD (outside card) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Tabs value={String(tab)} onValueChange={(v) => setTab(parseInt(v))}>
          <TabsList className="bg-gray-100">
            {TABS.map(t => (
              <TabsTrigger
                key={t.key}
                value={String(t.key)}
                className="data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:font-semibold"
                data-testid={`leads-tab-${t.key}`}
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-4 ml-auto">
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="rounded accent-teal-600"
              data-testid="leads-show-deactivated"
            />
            Show deactivated
          </label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search"
              className="pl-9 pr-3 py-1.5 w-64 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="leads-search"
            />
          </div>
          {can(myRights, "leads", "create") && <button
            onClick={() => setNewLeadInitial({})}
            className="inline-flex items-center justify-center rounded-md bg-[#1a7f6f] hover:bg-[#166a5c] text-white text-sm font-bold tracking-wider px-8 py-2.5 uppercase shadow-sm"
            data-testid="leads-new-btn"
          >
            New Lead
          </button>}
        </div>
      </div>

      {tab === 3 ? (
        <WorkInProgress />
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-gray-500 border-b">
              <tr>
                <SortableTh label={tab === 1 ? "Name" : "Client Name"} />
                <SortableTh label="Sub Broker Name" />
                <SortableTh label="Pincode" />
                <SortableTh label="City, State" />
                <SortableTh label="Date" />
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                  <Loader2 className="inline animate-spin mr-2" size={16}/> Loading…
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-14 text-center text-gray-400">
                  No records found.
                </td></tr>
              )}
              {filtered.map((r) => {
                const dead = !!r.is_deleted;
                return (
                <tr key={r.id}
                    className={`hover:bg-gray-50 ${dead ? "opacity-50 bg-gray-50/40" : ""}`}
                    data-testid={`lead-row-${r.id}`}>
                  <td className={`px-4 py-3 font-medium ${dead ? "text-gray-500 line-through" : "text-gray-800"}`}>
                    {tab === 1
                      ? `${r.first_name || ""} ${r.last_name || ""}`.trim()
                      : familyName(r)}
                    {dead && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-gray-200 text-gray-600 no-underline">Deactivated</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{r.associate_name || "—"}</td>
                  <td className="px-4 py-3 text-gray-600 tabular-nums">{r.pin_code || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {[r.city, r.state].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600 tabular-nums">
                    {(() => {
                      const iso = tab === 4 ? (r.account?.pushed_at || r.updated_at) : r.lead_date;
                      const d = daysLapsed(iso);
                      const pillClass =
                        d == null    ? "bg-gray-100 text-gray-500 border-gray-200" :
                        d === 0      ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        d <= 7       ? "bg-teal-50 text-teal-700 border-teal-200" :
                        d <= 30      ? "bg-amber-50 text-amber-700 border-amber-200" :
                                       "bg-rose-50 text-rose-700 border-rose-200";
                      return (
                        <div className="flex items-center gap-2">
                          <span>{fmtDate(iso)}</span>
                          {d != null && (
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${pillClass}`}
                              title={`${d} days since lead was received`}
                            >
                              {d === 0 ? "Today" : `${d}d ago`}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <ActionsCell tab={tab} row={r} onGo={openLead} deactivated={dead}/>
                  </td>
                  <td className="px-2 py-3 text-right">
                    <RowMenu
                      tab={tab}
                      deleted={dead}
                      onEdit={() => openLead(r, tab)}
                      onDelete={() => onDelete(r)}
                      onReactivate={() => onReactivate(r)}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 bg-white text-xs text-gray-500 border-t">
            <span>{filtered.length} total</span>
            <div className="flex items-center gap-1">
              <button className="p-1 rounded hover:bg-gray-100 disabled:opacity-40" disabled>|◀</button>
              <button className="p-1 rounded hover:bg-gray-100 disabled:opacity-40" disabled>◀</button>
              <span className="px-3 py-1 bg-teal-600 text-white rounded">1</span>
              <button className="p-1 rounded hover:bg-gray-100 disabled:opacity-40" disabled>▶</button>
              <button className="p-1 rounded hover:bg-gray-100 disabled:opacity-40" disabled>▶|</button>
            </div>
          </div>
        </div>
      )}

      {newLeadInitial && (
        <NewLeadModal
          initial={newLeadInitial.id ? newLeadInitial : null}
          onClose={() => setNewLeadInitial(null)}
          onSaved={(saved) => {
            const wasEdit = !!newLeadInitial.id;
            setNewLeadInitial(null);
            toast.success(wasEdit ? "Lead updated successfully." : "Lead Saved Successfully.");
            refresh();
          }}
        />
      )}

      {editLead && (
        <LeadDrawer
          lead={editLead}
          stage={drawerStage}
          setStage={setDrawerStage}
          onClose={() => { setEditLead(null); refresh(); }}
          onSaved={(u) => setEditLead(u)}
        />
      )}

      {deleteTarget && (
        <ConfirmDeactivateModal
          row={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

// ─── Deactivate confirmation modal (NO / YES) ─────────────────────
function ConfirmDeactivateModal({ row, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="deactivate-modal">
      <div className="bg-white rounded-md w-full max-w-md shadow-2xl">
        <div className="px-6 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-800">Confirm Deactivation</h3>
        </div>
        <div className="px-6 py-5 text-sm text-gray-700">
          Are you sure, you want to deactivate this record?
          <div className="mt-2 text-xs text-gray-500">
            {`${row.first_name || ""} ${row.last_name || ""}`.trim() || "This lead"} will be greyed out and can be reactivated later by editing.
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-3 border-t bg-gray-50 rounded-b-md">
          <button
            onClick={onCancel}
            className="px-6 py-1.5 rounded-md border border-gray-300 bg-white text-sm font-semibold uppercase tracking-wider text-gray-700 hover:bg-gray-100"
            data-testid="deactivate-no"
          >No</button>
          <button
            onClick={onConfirm}
            className="px-6 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold uppercase tracking-wider"
            data-testid="deactivate-yes"
          >Yes</button>
        </div>
      </div>
    </div>
  );
}

// ─── Kinntegra "Proceed to" pill — subtle mint bg + chevron right ────
function BadgePill({ color, children, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium hover:brightness-95 transition ${PILL[color] || PILL.info}`
      }
      data-testid={testid}
    >
      {children}
      <ChevronRight size={12} className="opacity-70"/>
    </button>
  );
}

// ─── Sortable column header (chevron up/down like kinntegra.co.in) ───
function SortableTh({ label }) {
  return (
    <th className="text-left px-4 py-3 font-semibold">
      <span className="inline-flex items-center gap-1.5">
        {label}
        <span className="text-gray-400 leading-none">⇅</span>
      </span>
    </th>
  );
}

// ─── "Proceed to:"  cell — matches kinntegra layout per tab ────
function ActionsCell({ tab, row, onGo, deactivated }) {
  // Deactivated leads always render a greyed-out disabled pill (per doc).
  const DisabledPill = ({ children, testid }) => (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
      data-testid={testid}
      title="Reactivate the lead to proceed"
    >
      {children}
      <ChevronRight size={12} className="opacity-40"/>
    </span>
  );

  if (tab === 1) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>Proceed to:</span>
        {deactivated
          ? <DisabledPill testid={`goto-intro-${row.id}-disabled`}>Introduction</DisabledPill>
          : <BadgePill color="info" onClick={() => onGo(row, 2)} testid={`goto-intro-${row.id}`}>Introduction</BadgePill>}
      </div>
    );
  }
  if (tab === 2) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>Proceed to:</span>
        {deactivated ? (
          <>
            <DisabledPill testid={`goto-data-${row.id}-disabled`}>Data Gathering</DisabledPill>
            <span className="h-5 border-l-2 border-gray-300 mx-1" />
            <DisabledPill testid={`goto-account-${row.id}-disabled`}>Account Opening</DisabledPill>
          </>
        ) : (
          <>
            <BadgePill color="danger" onClick={() => onGo(row, 3)} testid={`goto-data-${row.id}`}>Data Gathering</BadgePill>
            <span className="h-5 border-l-2 border-gray-300 mx-1" />
            <BadgePill color="danger" onClick={() => onGo(row, 4)} testid={`goto-account-${row.id}`}>Account Opening</BadgePill>
          </>
        )}
      </div>
    );
  }
  if (tab === 4) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>Proceed to:</span>
        {deactivated ? (
          <>
            <DisabledPill testid={`goto-data-${row.id}-disabled`}>Data Gathering</DisabledPill>
            <span className="h-5 border-l-2 border-gray-300 mx-1" />
            <DisabledPill testid={`goto-txn-${row.id}-disabled`}>Transaction</DisabledPill>
          </>
        ) : (
          <>
            <BadgePill color="danger" onClick={() => onGo(row, 3)} testid={`goto-data-${row.id}`}>Data Gathering</BadgePill>
            <span className="h-5 border-l-2 border-gray-300 mx-1" />
            <BadgePill color={row.account ? "success" : "danger"} onClick={() => onGo(row, 4)} testid={`goto-txn-${row.id}`}>Transaction</BadgePill>
          </>
        )}
      </div>
    );
  }
  return null;
}

// ─── 3-dot menu, matches ngbDropdown "btn btn-view bg-gray" ────
function RowMenu({ tab, onEdit, onDelete, onReactivate, deleted }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={() => setOpen(o => !o)}
        className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200"
        aria-label="Row actions"
      ><MoreVertical size={16}/></button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 rounded-md border bg-white shadow-lg z-10 text-sm">
          {deleted ? (
            <button
              onClick={() => { setOpen(false); onReactivate(); }}
              className="block w-full text-left px-3 py-2 text-emerald-700 hover:bg-emerald-50"
              data-testid="row-reactivate"
            >Reactivate Lead</button>
          ) : (
            <>
              <button
                onClick={() => { setOpen(false); onEdit(); }}
                className="block w-full text-left px-3 py-2 hover:bg-gray-100"
              >{tab === 1 ? "Edit Lead" : "Edit Family"}</button>
              <button
                onClick={() => { setOpen(false); onDelete(); }}
                className="block w-full text-left px-3 py-2 text-rose-600 hover:bg-rose-50"
                data-testid="row-deactivate"
              >{tab === 1 ? "Deactivate Lead" : "Deactivate Family"}</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Comprehensive Analysis tab — "Work In Progress" placeholder
function WorkInProgress() {
  return (
    <div className="bg-white border border-gray-200 border-t-0 rounded-b-md py-20 flex flex-col items-center justify-center text-center">
      <div className="w-24 h-24 rounded-full bg-amber-50 flex items-center justify-center mb-4">
        <Construction size={44} className="text-amber-500"/>
      </div>
      <h3 className="text-lg font-semibold text-gray-700">Work In Progress</h3>
      <p className="text-sm text-gray-500 mt-2 max-w-md">
        Comprehensive analysis (income, expense, goals &amp; recommendation) is under active development.
        Use the Introduction tab's "Data Gathering" pill to capture financials for now.
      </p>
    </div>
  );
}

// ─── New / Edit Lead modal — mirrors kinntegra design (Associate + Personal + Address) ──
function NewLeadModal({ onClose, onSaved, initial }) {
  const { api, user } = useAuth();
  const isSubBroker = (user?.role || "") === "sub_broker";
  const [form, setForm] = useState(initial || { ...EMPTY_LEAD });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [countries, setCountries] = useState([]);       // [{name, has_pincodes, dial}]
  const [genders, setGenders] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [pinQuery, setPinQuery] = useState("");
  const [pinLoading, setPinLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const countryRef = useRef(null);
  const [countryFlash, setCountryFlash] = useState(false);

  const activeCountry = useMemo(
    () => countries.find(c => c.name === form.country) || null,
    [countries, form.country]
  );
  const hasPincodes = !!activeCountry?.has_pincodes;

  // Load lookups on mount
  useEffect(() => {
    (async () => {
      try {
        const [a, c, g] = await Promise.all([
          api.get("/lead-mgmt/lookups/associates"),
          api.get("/lead-mgmt/lookups/countries"),
          api.get("/lead-mgmt/lookups/genders"),
        ]);
        setAssociates(a.data?.data || []);
        setCountries(c.data?.data || []);
        setGenders(g.data?.data || []);
        // Auto-pin the sub-broker to themselves (they can't pick anyone else)
        if (isSubBroker) {
          const rows = a.data?.data || [];
          const me = rows[0]; // /lookups/associates returns only self for sub_broker
          if (me && !form.associate_id) {
            setForm(f => ({ ...f, associate_id: me.id, associate_name: me.name || "" }));
          }
        }
      } catch (_) {}
    })();
    // eslint-disable-next-line
  }, []);

  // Refresh state list whenever country changes.
  useEffect(() => {
    if (!form.country) { setStates([]); return; }
    (async () => {
      try {
        const r = await api.get(`/lead-mgmt/lookups/states?country=${encodeURIComponent(form.country)}`);
        setStates(r.data?.data || []);
      } catch (_) { setStates([]); }
    })();
    // eslint-disable-next-line
  }, [form.country]);

  // Cities list — only for countries WITHOUT pincode support (UAE etc.);
  // for pincode-driven countries the city is auto-populated from the pin.
  useEffect(() => {
    if (!form.country || hasPincodes) { setCities([]); return; }
    (async () => {
      try {
        const q = form.state
          ? `?country=${encodeURIComponent(form.country)}&state=${encodeURIComponent(form.state)}`
          : `?country=${encodeURIComponent(form.country)}`;
        const r = await api.get(`/lead-mgmt/lookups/cities${q}`);
        setCities(r.data?.data || []);
      } catch (_) { setCities([]); }
    })();
    // eslint-disable-next-line
  }, [form.country, form.state, hasPincodes]);

  // Pincode typeahead debounced fetch (only when country supports pincodes).
  useEffect(() => {
    if (!hasPincodes || !form.country) return;
    let cancelled = false;
    setPinLoading(true);
    const t = setTimeout(async () => {
      try {
        const q = pinQuery ? `&q=${encodeURIComponent(pinQuery)}` : "";
        const r = await api.get(`/lead-mgmt/lookups/pincodes?country=${encodeURIComponent(form.country)}${q}&limit=30`);
        if (!cancelled) setPincodes(r.data?.data || []);
      } catch (_) { if (!cancelled) setPincodes([]); }
      finally { if (!cancelled) setPinLoading(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pinQuery, form.country, hasPincodes, api]);

  const flashCountry = () => {
    setCountryFlash(true);
    setTimeout(() => setCountryFlash(false), 900);
    // Scroll into view + focus
    try { countryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
  };

  const validateMobile = (v) => {
    if (!v) return "Please enter Mobile Number.";
    const m = String(v).replace(/[\s-]/g, "");
    const c = (form.country || "").toLowerCase();
    if (c === "india") {
      if (!/^\d+$/.test(m)) return "Please enter a valid Mobile Number.";
      if (m.length !== 10) return "Mobile Number must be exactly 10 digits.";
      if (!/^[6-9]/.test(m)) return "Mobile Number must start with 6, 7, 8, or 9.";
      return "";
    }
    return /^\+?\d{5,15}$/.test(m) ? "" : "Please enter a valid Mobile Number.";
  };

  const req = (v, msg) => (v && String(v).trim()) ? "" : msg;

  const validate = (draft = form) => {
    const e = {
      associate_id: req(draft.associate_id, "Please select a Sub Broker Name."),
      first_name:   req(draft.first_name,   "Please enter First Name."),
      last_name:    req(draft.last_name,    "Please enter Last Name."),
      gender:       req(draft.gender,       "Please select Gender."),
      address1:     req(draft.address1,     "Please enter Address Line 1."),
      country:      req(draft.country,      "Please select Country."),
      state:        req(draft.state,        "Please select State."),
      city:         req(draft.city,         "Please select City."),
      email:        req(draft.email, "Please enter Email ID.") || V.email(draft.email),
      mobile:       validateMobile(draft.mobile),
    };
    // Pincode: mandatory only when country supports it.
    if (hasPincodes) e.pin_code = req(draft.pin_code, "Please select Pincode.");
    Object.keys(e).forEach(k => { if (!e[k]) delete e[k]; });
    return e;
  };
  const set  = (k, v) => { const nx = { ...form, [k]: v }; setForm(nx); if (touched[k] || submitted) setErrors(validate(nx)); };
  const blur = (k)    => { setTouched(t => ({ ...t, [k]: true })); setErrors(validate()); };
  // Only surface errors for fields the user has actually interacted with
  // (or after they first pressed Proceed). Prevents premature reds on open.
  const errAt = (k) => ((touched[k] || submitted) ? errors[k] : undefined);

  const selectPincode = (row) => {
    const nx = {
      ...form,
      pin_code: row.pin,
      state: row.state || form.state || "",
      city: row.city || form.city || "",
    };
    setForm(nx);
    if (touched.pin_code || submitted) setErrors(validate(nx));
  };

  const save = async () => {
    setSubmitted(true);
    const errs = validate();
    setErrors(errs);
    setTouched({ associate_id:1, first_name:1, last_name:1, gender:1, address1:1,
                 country:1, pin_code:1, state:1, city:1, mobile:1, email:1 });
    if (Object.keys(errs).length) { toast.error("Please fix the highlighted fields"); return; }
    setBusy(true);
    try {
      const body = { ...form };
      if (initial?.id) body.id = initial.id;
      const { data } = await api.post("/lead-mgmt", body);
      if (data?.reactivated) toast.success("Lead reactivated on edit.");
      onSaved(data.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-800" data-testid="new-lead-title">
            {initial?.id ? "Edit Lead" : "New Lead"}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900 rounded p-1 border border-gray-200 hover:bg-gray-100"><X size={18}/></button>
        </div>

        <div className="p-6 space-y-8">
          {/* ── Other Details ── */}
          <section>
            <h3 className="text-sm font-bold uppercase tracking-wider text-teal-700 mb-4">Other Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={<>Sub Broker Name <span className="text-rose-500">*</span></>} error={errAt("associate_id")}>
                {isSubBroker ? (
                  // Sub-broker is themselves — show read-only pill, no dropdown.
                  <input
                    value={form.associate_name || user?.name || user?.email || ""}
                    readOnly
                    className="w-full px-3 py-2 border rounded-md text-sm bg-gray-50 text-gray-700 border-gray-300 cursor-not-allowed"
                    data-testid="fld-associate"
                    title="Sub Broker is auto-assigned to you"
                  />
                ) : (
                  <SearchableSelect
                    value={form.associate_id}
                    onChange={(id) => {
                      // ⚠️ Update BOTH fields in a single setState call — chaining
                      // two `set()` calls reads the same stale `form` snapshot
                      // and the second call clobbers `associate_id` back to "".
                      const a = associates.find(x => x.id === id);
                      const nx = { ...form, associate_id: id, associate_name: a?.name || "" };
                      setForm(nx);
                      if (touched.associate_id || submitted) setErrors(validate(nx));
                    }}
                    onBlur={() => blur("associate_id")}
                    error={errAt("associate_id")}
                    testid="fld-associate"
                    options={associates.map(a => {
                      const isRealCode = a.code && /^\d/.test(a.code);
                      return {
                        value: a.id,
                        label: isRealCode ? `${a.code} - ${a.name}` : (a.name || a.code || ""),
                        search: `${a.code || ""} ${a.name || ""}`.toLowerCase(),
                      };
                    })}
                    placeholder="Select sub broker"
                  />
                )}
              </Field>
            </div>
          </section>

          {/* ── Personal Information ── */}
          <section>
            <h3 className="text-sm font-bold uppercase tracking-wider text-teal-700 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label={<>First Name <span className="text-rose-500">*</span></>} error={errAt("first_name")}>
                <Input value={form.first_name} onChange={v=>set("first_name",v)} onBlur={()=>blur("first_name")} error={errAt("first_name")} testid="fld-first-name"/>
              </Field>
              <Field label={<>Last Name <span className="text-rose-500">*</span></>} error={errAt("last_name")}>
                <Input value={form.last_name} onChange={v=>set("last_name",v)} onBlur={()=>blur("last_name")} error={errAt("last_name")} testid="fld-last-name"/>
              </Field>
              <Field label={<>Gender <span className="text-rose-500">*</span></>} error={errAt("gender")}>
                <Select value={form.gender} onChange={v=>set("gender",v)} onBlur={()=>blur("gender")} error={errAt("gender")} testid="fld-gender"
                        options={genders.map(g => ({ value: g, label: g }))}
                        placeholder="Select gender"/>
              </Field>
            </div>
          </section>

          {/* ── Address Details ── */}
          <section>
            <h3 className="text-sm font-bold uppercase tracking-wider text-teal-700 mb-4">Address Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label={<>Address Line 1 <span className="text-rose-500">*</span></>} error={errAt("address1")}>
                <Input value={form.address1||""} onChange={v=>set("address1",v)} onBlur={()=>blur("address1")} error={errAt("address1")} testid="fld-addr1"/>
              </Field>
              <Field label="Address Line 2"><Input value={form.address2||""} onChange={v=>set("address2",v)} testid="fld-addr2"/></Field>
              <Field label="Address Line 3"><Input value={form.address3||""} onChange={v=>set("address3",v)} testid="fld-addr3"/></Field>

              <div ref={countryRef} className={countryFlash ? "animate-pulse ring-2 ring-amber-400 rounded-md" : ""}>
                <Field label={<>Country <span className="text-rose-500">*</span></>} error={errAt("country")}>
                  <Select value={form.country} onChange={(v)=>{
                    // Reset pin/state/city when country changes
                    const nx = { ...form, country: v, pin_code: "", state: "", city: "" };
                    setForm(nx);
                    if (touched.country || submitted) setErrors(validate(nx));
                  }} onBlur={()=>blur("country")} error={errAt("country")} testid="fld-country"
                          options={countries.map(c => ({ value: c.name, label: c.name }))}
                          placeholder="Select country"/>
                </Field>
              </div>

              <Field
                label={<>Pincode {hasPincodes ? <span className="text-rose-500">*</span> : <span className="text-gray-400 text-[10px]">(N/A)</span>} {pinLoading && <Loader2 className="inline animate-spin ml-1" size={12}/>}</>}
                error={errAt("pin_code")}
              >
                {hasPincodes ? (
                  <SearchableSelect
                    value={form.pin_code}
                    onChange={(pin) => {
                      const row = pincodes.find(p => p.pin === pin);
                      if (row) selectPincode(row);
                      else set("pin_code", pin);
                    }}
                    onQueryChange={setPinQuery}
                    onBlur={()=>blur("pin_code")}
                    error={errAt("pin_code")}
                    testid="fld-pin"
                    options={pincodes.map(p => ({
                      value: p.pin,
                      label: `${p.pin} — ${[p.city, p.state].filter(Boolean).join(", ")}`,
                      search: `${p.pin} ${p.city || ""} ${p.state || ""}`.toLowerCase(),
                    }))}
                    placeholder={form.country ? "Type first digits…" : "Select country first"}
                    disabled={!form.country}
                  />
                ) : (
                  <input value="" disabled placeholder="Not applicable for this country"
                         className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-400"
                         data-testid="fld-pin"/>
                )}
              </Field>

              <Field label={<>State <span className="text-rose-500">*</span></>} error={errAt("state")}>
                {hasPincodes ? (
                  // Editable — pincode auto-fills it as a best-effort suggestion,
                  // but the user can override if the pgeocode guess is wrong
                  // (e.g. India 421503 → we suggest a locality, user knows it's Badlapur).
                  <Input value={form.state || ""} onChange={v=>set("state", v)}
                         onBlur={()=>blur("state")} error={errAt("state")}
                         placeholder="Auto-filled from pincode (editable)"
                         testid="fld-state"/>
                ) : states.length > 0 ? (
                  <Select value={form.state} onChange={(v)=>{
                    // ⚠️ Single-setForm — chaining two set() calls reads
                    // the same stale form snapshot; the 2nd would clobber
                    // `state` back to "" (see Sub-Broker fix).
                    const nx = { ...form, state: v, city: "" };
                    setForm(nx);
                    if (touched.state || submitted) setErrors(validate(nx));
                  }} onBlur={()=>blur("state")} error={errAt("state")} testid="fld-state"
                          options={states.map(s => ({ value: s, label: s }))}
                          placeholder={form.country ? "Select state" : "Select country first"}
                          disabled={!form.country}/>
                ) : (
                  <Input value={form.state || ""} onChange={v=>set("state", v)}
                         onBlur={()=>blur("state")} error={errAt("state")}
                         placeholder={form.country ? "Enter state / region" : "Select country first"}
                         disabled={!form.country} testid="fld-state"/>
                )}
              </Field>

              <Field label={<>City <span className="text-rose-500">*</span></>} error={errAt("city")}>
                {hasPincodes ? (
                  <Input value={form.city || ""} onChange={v=>set("city", v)}
                         onBlur={()=>blur("city")} error={errAt("city")}
                         placeholder="Auto-filled from pincode (editable)"
                         testid="fld-city"/>
                ) : cities.length > 0 ? (
                  <Select value={form.city} onChange={v=>set("city", v)} onBlur={()=>blur("city")} error={errAt("city")} testid="fld-city"
                          options={cities.map(c => ({ value: c, label: c }))}
                          placeholder={form.state ? "Select city" : "Select state first"}
                          disabled={!form.state}/>
                ) : (
                  <Input value={form.city || ""} onChange={v=>set("city", v)}
                         onBlur={()=>blur("city")} error={errAt("city")}
                         placeholder={form.state ? "Enter city" : "Enter state first"}
                         disabled={!form.state} testid="fld-city"/>
                )}
              </Field>

              <Field label={<>Mobile Number <span className="text-rose-500">*</span></>} error={errAt("mobile")}>
                <div onClickCapture={(e) => {
                  if (!form.country) { e.preventDefault(); e.stopPropagation(); flashCountry(); }
                }}>
                  <Input
                    value={form.mobile||""}
                    onChange={v=>set("mobile", v)}
                    onBlur={()=>blur("mobile")}
                    error={errAt("mobile")}
                    disabled={!form.country}
                    testid="fld-mobile"
                    placeholder={form.country ? "" : "Select country first"}
                  />
                </div>
              </Field>

              <Field label={<>Email ID <span className="text-rose-500">*</span></>} error={errAt("email")}>
                <Input value={form.email||""} onChange={v=>set("email",v)} onBlur={()=>blur("email")} error={errAt("email")} testid="fld-email"/>
              </Field>
            </div>
          </section>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-8 py-2 rounded-md border border-gray-300 bg-white text-sm font-semibold uppercase text-gray-700 hover:bg-gray-100 tracking-wider" data-testid="new-lead-cancel-btn">Cancel</button>
          <button
            onClick={save} disabled={busy}
            className="px-10 py-2 rounded-md bg-[#1a7f6f] text-white text-sm font-bold uppercase hover:bg-[#166a5c] tracking-wider flex items-center gap-2 disabled:opacity-60"
            data-testid="new-lead-save-btn"
          >
            {busy ? <Loader2 className="animate-spin" size={14}/> : null}
            {initial?.id ? "Save" : "Proceed"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Lead detail / stage-progress drawer (right side, Bootstrap-style)
function LeadDrawer({ lead, stage, setStage, onClose, onSaved }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-3 z-10">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-800">
                {lead.introduction?.family_name || `${lead.first_name} ${lead.last_name}`}
              </h2>
              <div className="text-xs text-gray-500">
                {lead.email || "no email"} · {lead.mobile || "no mobile"}
              </div>
            </div>
            <button onClick={onClose} data-testid="lead-drawer-close" className="text-gray-500 hover:text-gray-900"><X size={18}/></button>
          </div>
          <nav className="flex border-b -mb-3">
            {TABS.map(t => {
              const active = t.key === stage;
              return (
                <button
                  key={t.key}
                  onClick={() => setStage(t.key)}
                  className={`px-4 py-2 text-xs border-b-2 -mb-px transition ${
                    active ? "border-indigo-600 text-indigo-700 font-semibold" : "border-transparent text-gray-500 hover:text-gray-800"
                  }`}
                  data-testid={`stepper-stage-${t.key}`}
                >{t.label}</button>
              );
            })}
          </nav>
        </div>

        <div className="p-5">
          {stage === 1 && <ProspectView lead={lead} onSaved={onSaved}/>}
          {stage === 2 && <IntroductionView lead={lead} onSaved={onSaved}/>}
          {stage === 3 && <ComprehensiveView lead={lead} onSaved={onSaved}/>}
          {stage === 4 && <AccountView lead={lead}/>}
        </div>
      </div>
    </div>
  );
}

// Stage 1 — inline prospect edit
function ProspectView({ lead, onSaved }) {
  const { api } = useAuth();
  const [form, setForm] = useState({ ...EMPTY_LEAD, ...lead });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/lead-mgmt", { ...form, id: lead.id });
      toast.success("Lead Saved Successfully."); onSaved(data.data);
    } catch (e) { toast.error("Save failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Row><Field label="First name"><Input value={form.first_name} onChange={v=>set("first_name", v)}/></Field>
           <Field label="Last name"><Input value={form.last_name} onChange={v=>set("last_name", v)}/></Field></Row>
      <Row><Field label="Mobile"><Input value={form.mobile||""} onChange={v=>set("mobile", v)}/></Field>
           <Field label="Email"><Input value={form.email||""} onChange={v=>set("email", v)}/></Field></Row>
      <Row><Field label="City"><Input value={form.city||""} onChange={v=>set("city", v)}/></Field>
           <Field label="State"><Input value={form.state||""} onChange={v=>set("state", v)}/></Field>
           <Field label="Pincode"><Input value={form.pin_code||""} onChange={v=>set("pin_code", v)}/></Field></Row>
      <button
        onClick={save} disabled={busy}
        className="px-4 py-1.5 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-60"
        data-testid="stage1-save-btn"
      >{busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save</button>
    </div>
  );
}

// Stage 2 — Data Gathering › Members (mirrored from kinntegraa.club)
const RELATIONSHIP_OPTIONS = ["Self", "Spouse", "Son", "Daughter", "Father", "Mother", "Brother", "Sister", "Other"];
const TAX_STATUS_OPTIONS = ["Resident", "NRI with Indian Passport", "NRI with Foreign Passport", "Foreign Passport"];
const TAX_REGIME_OPTIONS = ["Old Regime", "New Regime", "NA"];
const TAX_SLAB_OPTIONS = ["0%", "5%", "10%", "15%", "20%", "25%", "30%"];
const PROCEED_OPTIONS = ["Data Gathering"];

function IntroductionView({ lead, onSaved }) {
  const { api } = useAuth();
  const intro = lead.introduction || {};
  const seededPrimary = {
    id: 1,
    name: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(),
    dob: lead.dob || "",
    relation: "Self",
    life_expectancy: "",
    retirement_year: "",
    tax_status: "",
    tax_regime: "",
    tax_slab: "",
    is_primary: true,
  };
  const [members, setMembers] = useState(() => {
    const src = intro.members || [];
    if (!src.length) return [seededPrimary];
    return src.map((m, i) => ({
      id: i + 1,
      name: m.name || "",
      dob: m.dob || "",
      relation: m.relation || (i === 0 ? "Self" : ""),
      life_expectancy: m.life_expectancy != null ? String(m.life_expectancy) : "",
      retirement_year: m.retirement_year != null ? String(m.retirement_year) : "",
      tax_status: m.tax_status || "",
      tax_regime: m.tax_regime || "",
      tax_slab: m.tax_slab || "",
      is_primary: !!m.is_primary || (src.length === 1 && i === 0),
    }));
  });
  const [mfdRiaPartner, setMfdRiaPartner] = useState(intro.mfd_ria_partner || "");
  const [proceedOption, setProceedOption] = useState(intro.proceed_option || "");
  const [busy, setBusy] = useState(false);

  const familyName = useMemo(() => {
    const p = members.find(m => m.is_primary);
    return p?.name?.trim() ? `${p.name.trim()} & Family` : "";
  }, [members]);

  const upd  = (id, k, v) => setMembers(prev => prev.map(m => m.id === id ? { ...m, [k]: v } : m));
  const updM = (id, patch) => setMembers(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));
  const addMember = () => setMembers(prev => [...prev, {
    id: Math.max(...prev.map(x => x.id), 0) + 1,
    name: "", dob: "", relation: "", life_expectancy: "", retirement_year: "",
    tax_status: "", tax_regime: "", tax_slab: "", is_primary: false,
  }]);
  const removeMember = (id) => setMembers(prev => prev.filter(m => m.id !== id));
  const setPrimary = (id) => setMembers(prev => prev.map(m => ({
    ...m,
    is_primary: m.id === id,
    relation: m.id === id ? "Self" : (m.relation === "Self" ? "Spouse" : m.relation),
  })));

  const save = async () => {
    const primary = members.find(m => m.is_primary);
    if (!primary?.name?.trim()) return toast.error("Primary holder name is required");
    setBusy(true);
    try {
      const payload = {
        family_name: familyName || `${lead.first_name} & Family`,
        mfd_ria_partner: mfdRiaPartner,
        proceed_option: proceedOption,
        is_individual: true,
        is_non_individual: false,
        members: members.map(m => ({
          name: m.name,
          dob: m.dob || null,
          relation: m.relation || null,
          life_expectancy: m.life_expectancy === "" ? null : parseInt(m.life_expectancy),
          retirement_year: m.retirement_year === "" ? null : parseInt(m.retirement_year),
          tax_status: m.tax_status || null,
          tax_regime: m.tax_regime || null,
          tax_slab: m.tax_slab || null,
          is_primary: !!m.is_primary,
        })),
      };
      const { data } = await api.post(`/lead-mgmt/${lead.id}/introduction`, payload);
      toast.success("Family details saved.");
      onSaved(data.data);

      // Bridge to kinntegraa.club /broker/data-gathering when requested
      if (proceedOption === "Data Gathering") {
        try {
          const { data: br } = await api.post(`/lead-mgmt/${lead.id}/bridge/data-gathering`);
          // Stash prefill in sessionStorage under a name the target app
          // may read — will be no-op if the domain differs.
          try {
            sessionStorage.setItem("pendingLeadForFamily", JSON.stringify(br.prefill || {}));
          } catch (_) {}
          if (br.auto_login) {
            toast.success("Opening Data Gathering — auto-signed in.");
          } else {
            toast(`Opening Data Gathering. ${br.note || "Please sign in if prompted."}`,
                  { duration: 6000 });
          }
          window.open(br.redirect_url, "_blank", "noopener,noreferrer");
        } catch (e) {
          toast.error("Bridge to Data Gathering failed: " + (e?.response?.data?.detail || e.message));
        }
      }
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {/* Header block — mirrors kinntegraa.club */}
      <div>
        <h3 className="text-xl font-bold text-gray-900">Data Gathering</h3>
        <p className="text-sm text-gray-500 mt-0.5">Collect financial information</p>
      </div>

      {/* MFD/RIA Partner + Family Name (side by side) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm text-gray-600 mb-2 block">MFD/RIA Partner</label>
          <input
            value={mfdRiaPartner}
            onChange={(e) => setMfdRiaPartner(e.target.value)}
            placeholder="Select MFD/RIA Partner"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="intro-mfd-ria"
          />
        </div>
        <div>
          <label className="text-sm text-gray-600 mb-2 block">Family Name</label>
          <input
            value={familyName}
            readOnly
            placeholder="Auto-generated from primary holder"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-700"
            data-testid="intro-family-name"
          />
        </div>
      </div>

      {/* Family Members */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-gray-700">Family Members</label>
          <button
            type="button"
            onClick={addMember}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
            data-testid="intro-add-member"
          >
            <Plus size={14}/> Add Member
          </button>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Column header */}
          <div className="hidden lg:grid gap-2 px-4 py-3 bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b"
               style={{ gridTemplateColumns: '2fr 1.1fr 1fr 0.8fr 0.8fr 1.2fr 1.1fr 0.9fr 0.6fr 0.5fr' }}>
            <span>Name</span><span>DOB</span><span>Relation</span>
            <span>Life Exp.</span><span>Ret. Year</span>
            <span>Tax Status</span><span>Tax Regime</span><span>Tax Slab</span>
            <span className="text-center">Primary</span>
            <span className="text-center">Action</span>
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-100">
            {members.map((m) => {
              const foreign = m.tax_status === "Foreign Passport" || m.tax_status === "NRI with Foreign Passport";
              const setTaxStatus = (v) => {
                if (v === "Foreign Passport" || v === "NRI with Foreign Passport") {
                  updM(m.id, { tax_status: v, tax_regime: "NA", tax_slab: "0%" });
                } else {
                  upd(m.id, "tax_status", v);
                }
              };
              return (
                <div key={m.id}
                     className="grid grid-cols-1 lg:grid gap-2 px-4 py-3 items-center"
                     style={{ gridTemplateColumns: '2fr 1.1fr 1fr 0.8fr 0.8fr 1.2fr 1.1fr 0.9fr 0.6fr 0.5fr' }}
                     data-testid={`intro-member-row-${m.id}`}>
                  <input
                    value={m.name} onChange={(e) => upd(m.id, "name", e.target.value)}
                    placeholder="Enter name"
                    className="h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <input
                    type="date" value={m.dob || ""} onChange={(e) => upd(m.id, "dob", e.target.value)}
                    className="h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  {m.is_primary ? (
                    <input value="Self" readOnly className="h-9 px-3 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-700"/>
                  ) : (
                    <select value={m.relation || ""} onChange={(e) => upd(m.id, "relation", e.target.value)}
                            className="h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                      <option value="">Select</option>
                      {RELATIONSHIP_OPTIONS.filter(r => r !== "Self").map(r => <option key={r}>{r}</option>)}
                    </select>
                  )}
                  <input
                    type="number" min="50" max="120" placeholder="Years"
                    value={m.life_expectancy} onChange={(e) => upd(m.id, "life_expectancy", e.target.value)}
                    className="h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <input
                    type="number" min="2020" max="2100" placeholder="Year"
                    value={m.retirement_year} onChange={(e) => upd(m.id, "retirement_year", e.target.value)}
                    className="h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <select value={m.tax_status || ""} onChange={(e) => setTaxStatus(e.target.value)}
                          className="h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                    <option value="">Select</option>
                    {TAX_STATUS_OPTIONS.map(o => <option key={o}>{o}</option>)}
                  </select>
                  <select value={foreign ? "NA" : (m.tax_regime || "")} onChange={(e) => upd(m.id, "tax_regime", e.target.value)}
                          disabled={foreign}
                          className={`h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${foreign ? "bg-gray-100" : ""}`}>
                    <option value="">Select</option>
                    {TAX_REGIME_OPTIONS.map(o => <option key={o}>{o}</option>)}
                  </select>
                  <select value={foreign ? "0%" : (m.tax_slab || "")} onChange={(e) => upd(m.id, "tax_slab", e.target.value)}
                          disabled={foreign}
                          className={`h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${foreign ? "bg-gray-100" : ""}`}>
                    <option value="">Select</option>
                    {TAX_SLAB_OPTIONS.map(o => <option key={o}>{o}</option>)}
                  </select>
                  <div className="flex justify-center">
                    <input
                      type="radio" name={`primary-${lead.id}`}
                      checked={m.is_primary} onChange={() => setPrimary(m.id)}
                      className="h-4 w-4 accent-teal-600 cursor-pointer"
                      data-testid={`intro-primary-${m.id}`}
                    />
                  </div>
                  <div className="flex justify-center">
                    {!m.is_primary && (
                      <button onClick={() => removeMember(m.id)}
                              className="text-rose-500 hover:bg-rose-50 rounded-md p-1.5"
                              aria-label="Remove member">
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer: Next Step + Save & Next */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600">Next Step:</label>
          <select value={proceedOption} onChange={(e) => setProceedOption(e.target.value)}
                  className="w-64 h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="intro-next-step">
            <option value="">Select next step</option>
            {PROCEED_OPTIONS.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>
        <button
          onClick={save} disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-[#1a7f6f] hover:bg-[#166a5c] text-white text-sm font-semibold px-8 py-2 disabled:opacity-60"
          data-testid="stage2-save-btn"
        >
          {busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
          Save &amp; Next
        </button>
      </div>
    </div>
  );
}

// Stage 3 — Comprehensive
function ComprehensiveView({ lead, onSaved }) {
  const { api } = useAuth();
  const c = lead.comprehensive || {};
  const [form, setForm] = useState({
    annual_income: c.annual_income || 0,
    monthly_expense: c.monthly_expense || 0,
    monthly_surplus: c.monthly_surplus || 0,
    existing_investments: c.existing_investments || 0,
    existing_liabilities: c.existing_liabilities || 0,
    insurance_cover: c.insurance_cover || 0,
    risk_profile: c.risk_profile || "Moderate",
    life_goals: c.life_goals || [],
    notes: c.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const addGoal = () => setForm(f => ({...f, life_goals: [...f.life_goals, {name:"", target_amount:0, target_year:null, priority:"Medium"}]}));
  const upG = (i, k, v) => setForm(f => { const a=[...f.life_goals]; a[i]={...a[i],[k]:v}; return {...f, life_goals:a};});
  const rmG = (i) => setForm(f => ({...f, life_goals: f.life_goals.filter((_,idx)=>idx!==i)}));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lead-mgmt/${lead.id}/comprehensive`, form);
      toast.success("Comprehensive plan saved.");
      onSaved(data.data);
    } catch (e) { toast.error("Save failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Row>
        <Field label="Annual income (₹)"><Input type="number" value={form.annual_income} onChange={v=>setForm(f=>({...f, annual_income: parseFloat(v)||0}))}/></Field>
        <Field label="Monthly expense (₹)"><Input type="number" value={form.monthly_expense} onChange={v=>setForm(f=>({...f, monthly_expense: parseFloat(v)||0}))}/></Field>
        <Field label="Monthly surplus (₹)"><Input type="number" value={form.monthly_surplus} onChange={v=>setForm(f=>({...f, monthly_surplus: parseFloat(v)||0}))}/></Field>
      </Row>
      <Row>
        <Field label="Existing investments (₹)"><Input type="number" value={form.existing_investments} onChange={v=>setForm(f=>({...f, existing_investments: parseFloat(v)||0}))}/></Field>
        <Field label="Existing liabilities (₹)"><Input type="number" value={form.existing_liabilities} onChange={v=>setForm(f=>({...f, existing_liabilities: parseFloat(v)||0}))}/></Field>
        <Field label="Insurance cover (₹)"><Input type="number" value={form.insurance_cover} onChange={v=>setForm(f=>({...f, insurance_cover: parseFloat(v)||0}))}/></Field>
      </Row>
      <Row>
        <Field label="Risk profile">
          <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={form.risk_profile} onChange={e=>setForm(f=>({...f, risk_profile: e.target.value}))}>
            <option>Conservative</option><option>Moderate</option><option>Aggressive</option>
          </select>
        </Field>
      </Row>
      <div className="border border-gray-200 rounded-md p-4 bg-gray-50/60">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-gray-700 flex items-center gap-2 text-sm"><Target size={15}/> Life goals</div>
          <button onClick={addGoal} className="text-xs px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100">+ Add goal</button>
        </div>
        {form.life_goals.length === 0 && <div className="text-sm text-gray-400">No goals yet.</div>}
        {form.life_goals.map((g, i) => (
          <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-end">
            <Field label="Goal"><Input value={g.name} onChange={v=>upG(i,"name",v)}/></Field>
            <Field label="Target ₹"><Input type="number" value={g.target_amount} onChange={v=>upG(i,"target_amount",parseFloat(v)||0)}/></Field>
            <Field label="Year"><Input type="number" value={g.target_year||""} onChange={v=>upG(i,"target_year",parseInt(v)||null)}/></Field>
            <Field label="Priority">
              <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={g.priority||"Medium"} onChange={e=>upG(i,"priority",e.target.value)}>
                <option>High</option><option>Medium</option><option>Low</option>
              </select>
            </Field>
            <button onClick={()=>rmG(i)} className="text-rose-600 hover:bg-rose-50 rounded-md p-2 h-fit"><Trash2 size={14}/></button>
          </div>
        ))}
      </div>
      <Field label="Advisor notes">
        <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" rows={3} value={form.notes} onChange={e=>setForm(f=>({...f, notes: e.target.value}))}/>
      </Field>
      <button
        onClick={save} disabled={busy}
        className="px-4 py-1.5 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-60"
        data-testid="stage3-save-btn"
      >{busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save Plan</button>
    </div>
  );
}

// Stage 4 — Account (BSE stub)
function AccountView({ lead }) {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const push = async () => {
    setBusy(true);
    try { await api.post(`/lead-mgmt/${lead.id}/account/push`); toast.success("Pushed to BSE"); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-center gap-2 font-semibold text-amber-800 text-sm">
          <Briefcase size={16}/> BSE UCC push — awaiting RSA key provisioning
        </div>
        <div className="text-sm text-amber-800/80 mt-2">
          The Star MF v2 API requires a JOSE-encrypted payload signed with our RSA private key + BSE's public key.
          Once the keypair is issued and the <code className="bg-white px-1 rounded text-xs">X-API-Org-ID</code> fingerprint is set,
          this button will create the UCC, upload FATCA, and register the bank mandate in one call.
        </div>
        <button
          onClick={push} disabled={busy}
          className="mt-3 px-4 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm flex items-center gap-2 disabled:opacity-60"
          data-testid="stage4-push-btn"
        >{busy ? <Loader2 className="animate-spin" size={14}/> : <ArrowRight size={14}/>} Attempt UCC push</button>
      </div>
      {lead.account ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
          <div className="font-semibold text-emerald-800 text-sm flex items-center gap-2"><FileText size={14}/> BSE Account response</div>
          <pre className="text-xs mt-2 overflow-x-auto text-emerald-900">{JSON.stringify(lead.account, null, 2)}</pre>
        </div>
      ) : (
        <div className="text-sm text-gray-500">No BSE account created for this lead yet.</div>
      )}
    </div>
  );
}

// ─── Tiny form primitives ─────────────────────────────────────
function Row({ children }) { return <div className="grid grid-cols-2 gap-3 md:grid-cols-3">{children}</div>; }
function Field({ label, children, error }) {
  return <label className="block">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</div>
    {children}
    {error && <div className="text-[11px] text-rose-600 mt-1" data-testid="fld-error">{error}</div>}
  </label>;
}
function Input({ value, onChange, onBlur, type = "text", testid, error, disabled, placeholder }) {
  return <input
    type={type}
    value={value}
    onChange={(e)=>onChange(e.target.value)}
    onBlur={onBlur}
    disabled={disabled}
    placeholder={placeholder}
    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed ${
      error ? "border-rose-400 focus:ring-rose-400" : "border-gray-300 focus:ring-teal-500"
    }`}
    data-testid={testid}
  />;
}
function Select({ value, onChange, onBlur, options, placeholder, testid, error, disabled }) {
  return <select
    value={value || ""}
    onChange={(e)=>onChange(e.target.value)}
    onBlur={onBlur}
    disabled={disabled}
    className={`w-full px-3 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:text-gray-400 ${
      error ? "border-rose-400 focus:ring-rose-400" : "border-gray-300 focus:ring-teal-500"
    }`}
    data-testid={testid}
  >
    <option value="">{placeholder || "Select"}</option>
    {(options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>;
}

// Combobox with typeahead — used for long lists like the sub-broker roster
function SearchableSelect({ value, onChange, onBlur, onQueryChange, options, placeholder, testid, error, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  // ⚠️ Fire onBlur ONLY when this widget is currently open. Without the
  // `open` guard, EVERY mousedown anywhere on the page would fire this
  // component's onBlur (because we're outside our own ref) — which
  // incorrectly marks unrelated fields as "touched" and triggers their
  // validation errors (see Lead_Creation error_report1 — clicking a
  // Sub Broker option lit up Pincode's red border).
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, onBlur]);
  // Notify parent when the user types (used by the pincode field for server-side search)
  useEffect(() => { if (onQueryChange) onQueryChange(q); /* eslint-disable-next-line */ }, [q]);
  const selected = (options || []).find(o => o.value === value);
  const filtered = useMemo(() => {
    // When the parent supplies onQueryChange, it's already filtering server-side.
    if (onQueryChange) return options || [];
    const needle = q.toLowerCase().trim();
    if (!needle) return options || [];
    return (options || []).filter(o => (o.search || o.label.toLowerCase()).includes(needle));
  }, [options, q, onQueryChange]);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-md text-sm bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:text-gray-400 ${
          error ? "border-rose-400 focus:ring-rose-400" : "border-gray-300 focus:ring-teal-500"
        }`}
        data-testid={testid}
      >
        <span className={selected ? "text-gray-900" : "text-gray-400"}>
          {selected ? selected.label : (value ? value : (placeholder || "Select"))}
        </span>
        <ChevronRight size={14} className={`text-gray-400 transition-transform ${open ? "rotate-90" : ""}`}/>
      </button>
      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="p-2 border-b sticky top-0 bg-white">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type to filter…"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-gray-400">No matches</div>
            )}
            {filtered.map(o => (
              <button
                type="button"
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-teal-50 ${o.value === value ? "bg-teal-100 font-medium text-teal-800" : "text-gray-700"}`}
              >{o.label}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
